"use client";

import { motion } from "framer-motion";
import { ArrowUpRight, ShieldCheck, Database, FileText, Activity } from "lucide-react";
import Image from "next/image";

const projects = [
  {
    title: "Examination Score Management System",
    category: "Education & Grading",
    desc: "ระบบบริหารจัดการคะแนนสอบสำหรับวิทยาลัยพยาบาล รองรับการคำนวณเกรดที่ซับซ้อนตามมาตรฐานวิชาชีพ พร้อมระบบ Report สรุปผลอัตโนมัติ",
    image: "/images/photo-regrtn-1.png",
    tech: ["Angular", "PostgreSQL", "bootstrap 5"],
    stats: "ความถูกต้อง 100%",
    icon: <FileText className="text-fd-500" />
  },
  {
    title: "Pay for Performance System",
    category: "Financial & HR Management",
    desc: "ระบบบันทึกภาระงาน (P4P) สำหรับบุคลากรทางการแพทย์ คำนวณค่าตอบแทนตามการปฏิบัติงานจริง พร้อมสรุปยอดเงินรายเดือนอย่างแม่นยำ",
    image: "/images/photo-p4p-1.png",
    tech: ["Angular", "mysql", "bootstrap 5"],
    stats: "ลดเวลาทำสรุป 80%",
    icon: <Activity className="text-fd-500" />
  }
];

export default function ProjectsPage() {
  return (
    <div className="min-h-screen pt-12 mb-12 px-6 md:px-12 max-w-7xl mx-auto bg-background overflow-hidden">
      
      <section className="pt-32 pb-10 max-w-7xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >

          
          <h1 className="text-4xl md:text-5xl font-bold leading-[1.1] uppercase">
            ผลงาน <span className="text-fd-500 italic">ที่ผ่านมา</span> <br />
          </h1>
          <p className="md:text-lg md:text-2xl text-foreground/60 max-w-2xl font-medium">
            เปลี่ยนโจทย์ธุรกิจที่ซับซ้อน ให้เป็นระบบซอฟต์แวร์ที่แม่นยำ 
            ทรงพลัง และพร้อมเติบโตไปกับองค์กรของคุณ
          </p>
        </motion.div>
      </section>

      <section className="mx-auto space-y-12">
        {projects.map((project, i) => (
          <motion.div 
            key={i}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="group relative bg-foreground/[0.03] border border-border rounded-[3rem] rounded-[3rem] overflow-hidden hover:border-fd-500/30 transition-all duration-500"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* รูปภาพผลงาน */}
              <div className="relative h-[300px] lg:h-full min-h-[400px] overflow-hidden bg-black">
                <Image 
                  src={project.image} 
                  alt={project.title} 
                  unoptimized
                  fill 
                  className="object-cover brightness-[0.85] contrast-[1.1] group-hover:brightness-100 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-80 group-hover:opacity-40 transition-opacity duration-700" />

                {/* Badge สถิติความสำเร็จ */}
                <div className="absolute bottom-8 left-8 bg-white/10 backdrop-blur-md border border-white/20 p-4 rounded-2xl hidden md:block">
                  <p className="text-fd-500 font-black text-xl">{project.stats}</p>
                </div>
              </div>

              {/* รายละเอียดเนื้อหา */}
              <div className="p-8 md:p-16 flex flex-col justify-between">
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-fd-500/10 rounded-2xl">{project.icon}</div>
                    <span className="text-xs font-bold text-fd-500 uppercase tracking-widest">{project.category}</span>
                  </div>
                  <h3 className="text-3xl md:text-3xl font-bold uppercase leading-none">
                    {project.title}
                  </h3>
                  <p className="text-foreground/60 md:text-lg leading-relaxed font-medium">
                    {project.desc}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {project.tech.map((t) => (
                      <span key={t} className="px-3 py-1 bg-background border border-foreground/5 rounded-lg text-[12px] font-medium uppercase tracking-widest">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="mt-12 pt-8 border-t border-foreground/5 flex justify-between items-center">
                   <span className="text-xs font-bold uppercase text-foreground/30">Built by Folda Team</span>
                   {/*<button className="flex items-center gap-2 font-bold uppercase text-sm group-hover:text-fd-500 transition-colors">
                      Case Study <ArrowUpRight size={18} />
                   </button>*/}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </section>

      <section className="mt-16 py-16 md:py-24 px-8 relative overflow-hidden rounded-[3rem] md:rounded-[4rem]">
        <div className="absolute inset-0 bg-fd-500 dark:bg-fd-600 transition-colors duration-500" />
        
        <div className="absolute -top-24 -right-24 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-black/5 rounded-full blur-3xl" />

        <div className="relative z-10 max-w-5xl mx-auto flex flex-col lg:flex-row items-start lg:items-center justify-between gap-12 md:gap-16">
          
          <div className="space-y-6 text-white">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="space-y-4"
            >
              <h4 className="text-4xl md:text-6xl font-bold uppercase leading-[0.9]">
                ความแม่นยำ <br /> 
                <span className="opacity-80 italic text-3xl md:text-5xl">คือหัวใจของทุกระบบ</span>
              </h4>
              <div className="h-1 w-20 bg-white/30 rounded-full" />
              <p className="text-white/80 font-bold md:text-lg uppercase tracking-[0.2em] max-w-md leading-relaxed">
                High-precision Data & Financial Architecture <br />
                <span className="opacity-60 font-medium tracking-normal normal-case">มาตรฐานความปลอดภัยระดับสากล สำหรับองค์กรเฉพาะทาง</span>
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-2 gap-4 md:gap-6 w-full lg:w-auto">
            {[
              { icon: <ShieldCheck size={32} />, label: "Secure Access", desc: "ความปลอดภัยสูง" },
              { icon: <Database size={32} />, label: "Reliable Data", desc: "ข้อมูลแม่นยำ" }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                whileHover={{ y: -5, backgroundColor: "rgba(255, 255, 255, 0.15)" }}
                className="p-4 md:p-8 bg-white/10 backdrop-blur-md border border-white/10 rounded-[2rem] md:rounded-[2.5rem] flex flex-col items-center text-center space-y-3 w-full transition-colors"
              >
                <div className="text-white">
                  {item.icon}
                </div>
                <div className="space-y-1">
                  <p className="text-[16px] font-bold uppercase tracking-tight text-white">{item.label}</p>
                  <p className="text-[14px] font-medium uppercase opacity-60 text-white">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
          
        </div>
      </section>
    </div>
  );
}